import React, { useState } from 'react';
import { useForm } from '../../context/FormContext';
import { useSource } from '../../context/SourceContext';
import { useOperator } from '../../context/OperatorContext';
import { Button } from '../ui/button';
import { CheckCircle2, Loader2, Car, User, Briefcase, Home, CreditCard, Phone, Edit2, Building2, Send, AlertCircle, FileDown, ShieldAlert, AlertTriangle, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { computeFieldCompleteness } from '../../lib/fieldCompleteness';
import { detectEmailNameMismatch } from '../../lib/emailNameMismatch';
import { detectEmailTypo } from '../../lib/emailTypo';
import { validateAllSteps } from '../../lib/stepValidation';
import { isValidUKVehicleReg } from '../../lib/ukVehicleReg';
import { jsPDF } from 'jspdf';
import { getMotMileageWarning } from '../../lib/motMileage';
import { additionalIncomeLines, additionalIncomeTotal } from '../../lib/additionalIncome';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const calculateAge = (dobStr) => {
  if (!dobStr) return null;
  const parts = dobStr.split('/');
  if (parts.length !== 3) return null;
  const dob = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
  return age >= 0 && age < 120 ? age : null;
};

const AdditionalIncomeList = ({ formData }) => (
  <span className="block text-right" data-testid="review-additional-income">
    {additionalIncomeLines(formData).map((line) => <span key={line} className="block">{line}</span>)}
    {additionalIncomeLines(formData).length > 1 && <span className="block font-semibold">Total {additionalIncomeTotal(formData)}</span>}
  </span>
);

export const ReviewStep = () => {
  const { formData, goToStep, resetForm, setEditingFromReview, extractedData, updateFormData, applicationMode } = useForm();
  const { getSubmitUrl, source } = useSource();
  const noReg = source?.submission_mode === 'pdf_email' && !!formData.vehicle_reg_unknown;
  const { isOperator, consentRef, setConsentRef, consentConfirmed, setConsentConfirmed, sessionId } = useOperator();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingToAutoConvert, setIsSubmittingToAutoConvert] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [submissionErrors, setSubmissionErrors] = useState(null);
  // Pre-submit validation errors collected by validateAllSteps. Rendered as a
  // clickable list near the submit button so a Skip-to-Review user can see
  // every blocker at once and jump straight to each failing step.
  const [validationErrors, setValidationErrors] = useState([]);
  // True when the original broker submission failed but the backup PDF email succeeded.
  // Drives the "honest notice" variant of the success screen.
  const [submissionViaFallback, setSubmissionViaFallback] = useState(false);

  const handleEditSection = (stepIndex) => {
    setEditingFromReview(true);
    goToStep(stepIndex);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmissionErrors(null);
    try {
      const response = await axios.post(`${API_URL}/applications`, formData);
      if (response.data) {
        toast.success('Application saved locally!');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save application');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Parse broker errors (AutoConvert OR Artis/Jigsaw) into friendly field/message pairs
  const parseAutoConvertErrors = (errorData) => {
    const errors = [];
    const stringify = (v) => typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v || '');

    // Convert PascalCase / dotted Jigsaw field paths into a friendly label.
    // e.g. "jigsawReferral.MainApplicant.DrivingLicence" -> "Driving Licence"
    //      "jigsawReferral.Business.BankAccount.BranchBIC" -> "Business Bank Branch B I C"
    const humanizeField = (raw) => {
      if (!raw) return 'General';
      let label = String(raw)
        .replace(/^jigsawReferral\.?/i, '')
        .replace(/^Applicants\[\d+\]\./, '')
        .replace(/Addresses\[\d+\]\./i, 'Address ')
        .replace(/EmploymentHistory\[\d+\]\./i, 'Employment ')
        .replace(/\[\d+\]/g, '')
        .replace(/\./g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .trim();
      return label || 'General';
    };

    try {
      const parsed = typeof errorData === 'string' ? JSON.parse(errorData) : errorData;

      // --- Artis (Jigsaw) shape 1: { Message: "...", ModelState: { "field.path": "msg" | ["msg"] } }
      if (parsed && parsed.ModelState && typeof parsed.ModelState === 'object') {
        for (const [field, msg] of Object.entries(parsed.ModelState)) {
          const msgs = Array.isArray(msg) ? msg.map(stringify) : [stringify(msg)];
          errors.push({ field: humanizeField(field), messages: msgs });
        }
      }

      // --- AutoConvert shape: { Errors: [...] | { field: [msg] } }
      const errorArray = parsed?.Errors || parsed?.errors;
      if (Array.isArray(errorArray)) {
        errorArray.forEach(msg => {
          const msgStr = stringify(msg);
          const match = msgStr.match(/'([^']+)'/);
          const field = match ? humanizeField(match[1]) : 'General';
          errors.push({ field, messages: [msgStr.replace(/'[^']+'\s*/, '').trim()] });
        });
      } else if (errorArray && typeof errorArray === 'object') {
        for (const [field, messages] of Object.entries(errorArray)) {
          const msgArr = Array.isArray(messages) ? messages.map(stringify) : [stringify(messages)];
          errors.push({ field: humanizeField(field), messages: msgArr });
        }
      }

      // --- Artis shape 2: flat object at root where keys ARE field paths, values are error strings
      // e.g. { "JigsawReferral.MainApplicant.DrivingLicence": "Required" }
      if (errors.length === 0 && parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        for (const [key, val] of Object.entries(parsed)) {
          if (key === 'Message' || key === 'title') continue;
          const isStr = typeof val === 'string';
          const isStrArray = Array.isArray(val) && val.every(v => typeof v === 'string');
          if (isStr || isStrArray) {
            errors.push({ field: humanizeField(key), messages: isStr ? [val] : val });
          }
        }
      }

      // Top-level summary message (Artis "Message" or AutoConvert "title")
      if (errors.length === 0 && (parsed?.Message || parsed?.title)) {
        errors.push({ field: 'General', messages: [stringify(parsed.Message || parsed.title)] });
      }

      // Raw fallback if nothing parsed
      if (errors.length === 0 && errorData) {
        errors.push({ field: 'Error', messages: [stringify(errorData)] });
      }
    } catch {
      errors.push({ field: 'Error', messages: [stringify(errorData)] });
    }
    return errors;
  };

  // Build a FormData payload containing the proposal PDF plus the metadata that the
  // backend needs to format the email subject/body. Reused by both the existing
  // pdf_email submission mode AND the new failure-fallback path.
  const buildPdfEmailPayload = (extras = {}) => {
    const doc = generatePDFDoc();
    const pdfBase64 = doc.output('datauristring').split(',')[1];
    const applicantName = `${formData.title || ''} ${formData.first_name || ''} ${formData.surname || ''}`.trim();
    const sourceName = source.trading_name || source.name || source.slug;
    const filename = `${sourceName.replace(/[^a-zA-Z0-9]/g, '_')}_${applicantName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;

    const payload = new FormData();
    payload.append('pdf_base64', pdfBase64);
    payload.append('filename', filename);
    payload.append('applicant_name', applicantName);
    payload.append('dealer_name', formData.dealer_name || '');
    payload.append('vehicle_registration', noReg ? '' : (formData.vehicle_registration || ''));
    payload.append('vehicle_make', formData.vehicle_make || '');
    if (noReg) payload.append('vehicle_description', formData.vehicle_description || '');
    payload.append('finance_type', formData.finance_type || 'HP');
    payload.append('cash_price', formData.cash_price || '0');
    payload.append('deposit', formData.deposit || '0');
    payload.append('part_exchange', formData.part_exchange || '0');
    payload.append('settlement', formData.settlement || '0');
    Object.entries(extras).forEach(([k, v]) => payload.append(k, v ?? ''));
    return payload;
  };

  // Fire-and-forget PDF copy email after a SUCCESSFUL broker submission. Skipped silently
  // when the per-source toggle is off, or when no recipients are configured. Errors here
  // never bubble up to the customer's success screen — the proposal is already submitted.
  const tryPdfCopyForDealer = async (acReference, operatorHeaders) => {
    if (!source?.send_pdf_copy_enabled) return;
    // Need at least one recipient (pdf_copy_email or fallback notify_email)
    if (!source?.pdf_copy_email && !source?.notify_email) return;
    try {
      const payload = buildPdfEmailPayload({ ac_reference: acReference || '' });
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/submit-pdf-copy/${source.slug}`,
        payload,
        { headers: operatorHeaders }
      );
    } catch (e) {
      // Silent — already submitted; don't disturb the user
      console.warn('PDF copy email failed (proposal already submitted OK):', e);
    }
  };

  // When the broker API rejects/errors, try to email the dealer the proposal PDF
  // so the application is not lost. Returns true if fallback succeeded.
  const tryFailureFallback = async (failureReason, operatorHeaders) => {
    if (source?.fallback_pdf_enabled === false) return false;
    // Need a destination email — bail silently if neither notify_email nor submission_email is configured
    if (!source?.notify_email && !source?.submission_email) return false;
    try {
      const brokerLabel = source?.broker_name
        || ((source?.broker_system || '').toLowerCase() === 'artis' ? 'Jigsaw Finance' : 'the broker');
      const payload = buildPdfEmailPayload({
        broker_attempted: brokerLabel,
        failure_reason: failureReason || 'Unknown error',
      });
      const resp = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/submit-failure-fallback/${source.slug}`,
        payload,
        { headers: operatorHeaders }
      );
      return Boolean(resp.data?.success);
    } catch (fallbackErr) {
      console.error('Fallback PDF email failed:', fallbackErr);
      return false;
    }
  };

  const handleSubmitToAutoConvert = async () => {
    // Defensive double-submit guard. The button already has a `disabled` prop
    // tied to isSubmittingToAutoConvert, but a fast double-click before the
    // React re-render commits can still fire the handler twice. Bail early
    // if a submission is already in flight.
    if (isSubmittingToAutoConvert) return;

    // Run the SAME validateStep rules used by the per-step Next button gating
    // (see /app/frontend/src/lib/stepValidation.js). This guarantees a
    // Skip-to-Review user cannot bypass the same checks — catches malformed
    // postcodes, sub-18 DOB, missing vehicle make from a failed DVLA lookup,
    // bad employer postcodes etc. before the broker rejects them.
    const stepErrors = validateAllSteps(formData, applicationMode, { allowNoReg: source?.submission_mode === 'pdf_email' });
    if (stepErrors.length > 0) {
      const first = stepErrors[0];
      toast.error(`Cannot submit — ${stepErrors.length === 1 ? 'fix the issue below' : `${stepErrors.length} issues to fix below`}`);
      // Render the full list near the submit button so the user sees every
      // blocker at once and can click any item to jump straight to it.
      setValidationErrors(stepErrors);
      // Scroll the validation panel into view (it's just above the submit button).
      setTimeout(() => {
        const el = document.querySelector('[data-testid="pre-submit-validation-errors"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }
    // Clear any stale validation list from a previous attempt.
    setValidationErrors([]);

    // Operator Mode: require explicit consent confirmation before submitting
    if (isOperator && !consentConfirmed) {
      toast.error('Please confirm that the dealer has obtained customer consent before submitting.');
      return;
    }

    // Build operator headers (sent on every submission when in operator mode)
    const operatorHeaders = isOperator
      ? {
          'X-Operator-Mode': 'true',
          'X-Operator-Consent-Ref': consentRef || '',
          'X-Operator-Session': sessionId || '',
        }
      : {};

    setIsSubmittingToAutoConvert(true);
    setSubmissionErrors(null);
    setSubmissionViaFallback(false);
    try {
      // Check if this source uses PDF email mode
      if (source.submission_mode === 'pdf_email') {
        const formPayload = buildPdfEmailPayload();
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/submit-pdf-email/${source.slug}`,
          formPayload,
          { headers: operatorHeaders }
        );

        if (response.data.success) {
          setIsSubmitted(true);
          setSubmissionResult(response.data);
          toast.success('Application submitted via email successfully!');
        } else {
          toast.error('Failed to send application email');
        }
      } else {
        // Standard API submission (AutoConvert or Artis). When applicationMode is
        // 'credit-line' we tag the payload so the backend knows to omit vehicle
        // details (Artis will assign a dummy car) and so we can analytics-split
        // these soft-search submissions later.
        const submissionPayload = { ...formData, application_mode: applicationMode };
        const response = await axios.post(getSubmitUrl(), submissionPayload, { headers: operatorHeaders });

        if (response.data.success) {
          setIsSubmitted(true);
          setSubmissionResult(response.data);
          toast.success(`Application submitted to ${source.trading_name || source.name || 'broker'} successfully!`);
          // Fire-and-forget: send a PDF copy to the dealer for their records (per-source toggle)
          tryPdfCopyForDealer(response.data.enquiry_reference, operatorHeaders);
        } else {
          // Broker rejected the payload — try the PDF backup so the application isn't lost
          const errors = parseAutoConvertErrors(response.data.error);
          setSubmissionErrors(errors);
          const failureReason = typeof response.data.error === 'string'
            ? response.data.error
            : JSON.stringify(response.data.error || {}).slice(0, 800);
          const fallbackOk = await tryFailureFallback(failureReason, operatorHeaders);
          if (fallbackOk) {
            setIsSubmitted(true);
            setSubmissionViaFallback(true);
            setSubmissionResult({ fallback_sent: true });
            toast.success('Your details were captured and sent to your dealer');
          } else {
            toast.error('Submission failed - please check the errors below');
          }
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || error.message || 'Submission failed';
      const errors = parseAutoConvertErrors(errorMsg);
      setSubmissionErrors(errors);
      // Network/5xx/timeout — try the PDF backup as a last resort. Skip if this was already
      // the pdf_email submission mode (no fallback path there; the email itself failed).
      if (source.submission_mode !== 'pdf_email') {
        const fallbackOk = await tryFailureFallback(
          typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg).slice(0, 800),
          operatorHeaders
        );
        if (fallbackOk) {
          setIsSubmitted(true);
          setSubmissionViaFallback(true);
          setSubmissionResult({ fallback_sent: true });
          toast.success('Your details were captured and sent to your dealer');
        } else {
          toast.error('Submission failed - please check the errors below');
        }
      } else {
        toast.error('Submission failed - please check the errors below');
      }
    } finally {
      setIsSubmittingToAutoConvert(false);
    }
  };

  const generatePDFDoc = () => {
      // eslint-disable-next-line new-cap
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 14;
      const contentWidth = pageWidth - margin * 2;
      const halfWidth = (contentWidth - 6) / 2;
      const pageBottom = 275;
      let yPos = 20;

      const hexToRgb = (hex) => {
        const h = hex.replace('#', '');
        return { r: parseInt(h.substring(0, 2), 16), g: parseInt(h.substring(2, 4), 16), b: parseInt(h.substring(4, 6), 16) };
      };
      const brandColor = hexToRgb(source?.primary_color || '#0066CC');
      const sourceName = source?.company_name || source?.name || 'Finance Application';

      const ensureSpace = (needed) => {
        if (yPos + needed > pageBottom) {
          doc.addPage();
          yPos = 20;
        }
      };

      // Section header with brand-coloured underline
      const addSectionHeader = (title) => {
        ensureSpace(16);
        yPos += 5;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(brandColor.r, brandColor.g, brandColor.b);
        doc.text(title, margin, yPos);
        yPos += 2;
        doc.setDrawColor(brandColor.r, brandColor.g, brandColor.b);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 5;
      };

      // Two-column row inside a box
      const addRow = (label, value, x, w) => {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(120, 120, 120);
        doc.text(label, x + 3, yPos);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(30, 30, 30);
        const valStr = String(value || '-');
        const wrapped = doc.splitTextToSize(valStr, w - 6);
        doc.text(wrapped, x + 3, yPos + 4);
        return 4 + wrapped.length * 4;
      };

      // Add a pair of fields side by side in boxes
      const addFieldPair = (label1, value1, label2, value2) => {
        ensureSpace(16);
        const boxH = 14;
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.2);
        doc.roundedRect(margin, yPos - 3, halfWidth, boxH, 1, 1, 'S');
        addRow(label1, value1, margin, halfWidth);
        if (label2) {
          doc.roundedRect(margin + halfWidth + 6, yPos - 7 - 4, halfWidth, boxH, 1, 1, 'S');
          const saveY = yPos;
          yPos = yPos - 8;
          addRow(label2, value2, margin + halfWidth + 6, halfWidth);
          yPos = saveY;
        }
        yPos += 4;
      };

      // Single full-width field
      const addFullField = (label, value) => {
        ensureSpace(16);
        const boxH = 14;
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.2);
        doc.roundedRect(margin, yPos - 3, contentWidth, boxH, 1, 1, 'S');
        addRow(label, value, margin, contentWidth);
        yPos += 4;
      };

      // Simple label: value row (no box)
      const addLine = (label, value) => {
        const valStr = String(value || '-');
        doc.setFontSize(8.5);
        ensureSpace(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(120, 120, 120);
        doc.text(label, margin, yPos);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 30, 30);
        doc.text(valStr, margin + 55, yPos);
        yPos += 5;
      };

      // Thin separator
      const addSubSeparator = () => {
        ensureSpace(6);
        yPos += 1;
        doc.setDrawColor(210, 210, 210);
        doc.setLineWidth(0.15);
        doc.line(margin + 4, yPos, pageWidth - margin - 4, yPos);
        yPos += 3;
      };

      // ---- Compact Document Header ----
      // Slim top rule (0.6mm) instead of a chunky banner strip
      doc.setFillColor(brandColor.r, brandColor.g, brandColor.b);
      doc.rect(0, 0, pageWidth, 0.6, 'F');

      yPos = 11;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(brandColor.r, brandColor.g, brandColor.b);
      doc.text(sourceName, margin, yPos);
      // Right-aligned generated-date on the same line
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(140, 140, 140);
      doc.text(
        `Generated: ${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`,
        pageWidth - margin,
        yPos,
        { align: 'right' },
      );
      yPos += 4;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text('Vehicle Finance Application', margin, yPos);
      if (formData.dealer_name) {
        doc.setTextColor(120, 120, 120);
        doc.setFontSize(8);
        doc.text(`Dealer: ${formData.dealer_name}`, pageWidth - margin, yPos, { align: 'right' });
      }
      yPos += 2.5;
      doc.setDrawColor(220, 222, 228);
      doc.setLineWidth(0.2);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 4;

      // ============================================================
      // CARD-BASED LAYOUT HELPERS
      // ============================================================
      // Each "card" is a bordered box with a coloured header strip and a
      // list of label/value rows. We can compose cards side-by-side (2-col)
      // or full-width. Cards auto-flow across pages when they don't fit.

      // Measure the height a card would take without drawing it, so we can
      // decide whether to page-break BEFORE starting to draw.
      const measureCard = (w, title, rows) => {
        const pad = 4;
        const titleH = 7;
        let contentH = 2.5;
        const labelW = Math.min(38, w * 0.34);
        const valueW = w - pad * 2 - labelW - 2;
        rows.forEach(r => {
          const valStr = r.value == null || r.value === '' ? '-' : String(r.value);
          if (r.heading) {
            contentH += 5.5;
          } else if (r.multi) {
            const wrapped = doc.splitTextToSize(valStr, w - pad * 2);
            contentH += 3.5 + wrapped.length * 4 + 2.5;
          } else {
            const wrapped = doc.splitTextToSize(valStr, valueW);
            contentH += Math.max(5.6, wrapped.length * 4 + 1.5);
          }
        });
        return titleH + pad + contentH + pad;
      };

      const drawCardAt = (x, y, w, title, rows, forceH = null) => {
        const pad = 4;
        const titleH = 7;
        const naturalH = measureCard(w, title, rows);
        const totalH = forceH != null ? forceH : naturalH;
        // Body background + border — drawn at forced height for side-by-side alignment
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(210, 213, 220);
        doc.setLineWidth(0.2);
        doc.roundedRect(x, y, w, totalH, 1.5, 1.5, 'FD');
        // Title strip
        doc.setFillColor(brandColor.r, brandColor.g, brandColor.b);
        doc.roundedRect(x, y, w, titleH, 1.5, 1.5, 'F');
        doc.rect(x, y + titleH - 1.5, w, 1.5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(255, 255, 255);
        doc.text(title.toUpperCase(), x + pad, y + 5);
        // Rows — normal weight for a lighter, less cramped feel
        const labelW = Math.min(38, w * 0.34);
        const valueW = w - pad * 2 - labelW - 2;
        let ry = y + titleH + pad + 3;
        rows.forEach(r => {
          if (r.heading) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(6.5);
            doc.setTextColor(brandColor.r, brandColor.g, brandColor.b);
            doc.text(String(r.heading).toUpperCase(), x + pad, ry);
            ry += 5;
            return;
          }
          const valStr = r.value == null || r.value === '' ? '-' : String(r.value);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.5);
          doc.setTextColor(130, 133, 140);
          doc.text(r.label, x + pad, ry);
          if (r.emphasise) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8.5);
            doc.setTextColor(brandColor.r, brandColor.g, brandColor.b);
          } else {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8.5);
            doc.setTextColor(45, 48, 55);
          }
          if (r.multi) {
            const wrapped = doc.splitTextToSize(valStr, w - pad * 2);
            ry += 4;
            doc.text(wrapped, x + pad, ry);
            ry += wrapped.length * 4 + 2.5;
          } else {
            const wrapped = doc.splitTextToSize(valStr, valueW);
            doc.text(wrapped, x + pad + labelW, ry);
            ry += Math.max(5.6, wrapped.length * 4 + 1.5);
          }
        });
        return y + totalH;
      };

      const drawTwoCards = (leftCard, rightCard) => {
        const cardW = halfWidth;
        const leftH = leftCard ? measureCard(cardW, leftCard.title, leftCard.rows) : 0;
        const rightH = rightCard ? measureCard(cardW, rightCard.title, rightCard.rows) : 0;
        // Equalise card heights so bottom edges align — kills the jagged look
        const rowH = Math.max(leftH, rightH);
        if (yPos + rowH > pageBottom) {
          doc.addPage();
          yPos = 20;
        }
        if (leftCard) drawCardAt(margin, yPos, cardW, leftCard.title, leftCard.rows, rowH);
        if (rightCard) drawCardAt(margin + halfWidth + 6, yPos, cardW, rightCard.title, rightCard.rows, rowH);
        yPos += rowH + 5;
      };

      const drawFullCard = (card) => {
        const needed = measureCard(contentWidth, card.title, card.rows);
        if (yPos + needed > pageBottom) {
          doc.addPage();
          yPos = 20;
        }
        yPos = drawCardAt(margin, yPos, contentWidth, card.title, card.rows) + 5;
      };

      const money = (v) => v == null || v === '' ? '-' : `\u00A3${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

      // ============================================================
      // BUILD CARD CONTENT
      // ============================================================
      // Layout strategy — money-critical first, bank details LAST (per user
      // feedback: bank isn't always filled in, so it shouldn't hog page 1).
      //   Row 1: [Customer]        |  [Vehicle]
      //   Row 2: [Finance Summary] |  [Current Address]
      //   Row 3: [Current Employment] | [Income & Affordability]  (or null)
      //   Row 4+: Previous Address N | Previous Employment N  (per index)
      //   Full:   Business Details, Additional Notes
      //   Last:   Bank Details (full-width, so a lonely bank card doesn't
      //           leave a half-empty row at the end)
      const applicantAge = calculateAge(formData.date_of_birth);

      // ----- Customer -----
      const customerRows = [
        { label: 'Name', value: `${formData.title || ''} ${formData.first_name || ''} ${formData.surname || ''}`.trim() },
        { label: 'Date of Birth', value: `${formData.date_of_birth || '-'}${applicantAge !== null ? ` (${applicantAge} yrs)` : ''}` },
        { label: 'Gender', value: formData.gender },
        { label: 'Nationality', value: formData.nationality },
        { label: 'Marital', value: formData.marital_status },
        { label: 'Dependants', value: formData.dependants?.toString() || '0' },
        { label: 'Licence', value: formData.driving_licence_type },
        { label: 'UK Passport', value: formData.has_uk_passport === true ? 'Yes' : formData.has_uk_passport === false ? 'No' : '-' },
        { label: 'Mobile', value: formData.mobile_number },
        { label: 'Email', value: formData.email_address, multi: true },
      ];

      // ----- Vehicle -----
      const vehicleRows = [
        noReg && { label: 'Vehicle Description', value: formData.vehicle_description, multi: true, emphasise: true },
        { label: 'Registration', value: noReg ? 'Not provided' : formData.vehicle_registration, emphasise: true },
        { label: 'Type', value: formData.vehicle_type },
        formData.vehicle_condition && { label: 'Condition', value: formData.vehicle_condition },
        formData.vehicle_make && { label: 'Make', value: formData.vehicle_make },
        formData.vehicle_model && { label: 'Model', value: formData.vehicle_model },
        formData.vehicle_derivative && { label: 'Derivative', value: formData.vehicle_derivative, multi: true },
        formData.vehicle_colour && { label: 'Colour', value: formData.vehicle_colour },
        formData.vehicle_year && { label: 'Year', value: formData.vehicle_year },
        formData.vehicle_fuel_type && { label: 'Fuel', value: formData.vehicle_fuel_type },
        formData.vehicle_engine_capacity && { label: 'Engine', value: `${formData.vehicle_engine_capacity}cc` },
        formData.vehicle_date_first_registered && { label: 'First Reg.', value: formData.vehicle_date_first_registered },
        formData.vehicle_mileage && { label: 'Mileage', value: `${Number(formData.vehicle_mileage).toLocaleString('en-GB')} mi` },
        formData.mot_last_mileage && { label: 'Last MOT Mileage', value: `${Number(formData.mot_last_mileage).toLocaleString('en-GB')} mi (${formData.mot_last_date})` },
        getMotMileageWarning(formData) && { label: 'Mileage Warning', value: getMotMileageWarning(formData), multi: true, emphasise: true },
        { label: 'VAT Qualifying', value: formData.vat_qualifying ? 'Yes' : 'No', emphasise: formData.vat_qualifying },
      ].filter(Boolean);

      // ----- Finance Summary (money-critical — kept high on page 1) -----
      const rawPrice = parseFloat(formData.cash_price) || 0;
      const cashPrice = formData.vat_qualifying ? rawPrice * 1.2 : rawPrice;
      const deposit = parseFloat(formData.deposit) || 0;
      const partExchange = parseFloat(formData.part_exchange) || 0;
      const settlement = parseFloat(formData.settlement) || 0;
      const balanceToFinance = cashPrice - deposit - partExchange + settlement;

      const financeRows = [
        { label: formData.vat_qualifying ? 'Cash Price (inc VAT)' : 'Cash Price', value: money(cashPrice) },
      ];
      if (formData.vat_qualifying) {
        financeRows.push({ label: 'Net (ex-VAT)', value: money(rawPrice) });
        financeRows.push({ label: 'VAT (20%)', value: money(rawPrice * 0.2) });
      }
      financeRows.push({ label: 'Deposit', value: money(deposit) });
      if (partExchange) financeRows.push({ label: 'Part Exchange', value: money(partExchange) });
      if (settlement) financeRows.push({ label: 'Settlement', value: money(settlement) });
      financeRows.push({ label: 'Balance to Finance', value: money(balanceToFinance), emphasise: true });
      financeRows.push({ label: 'Finance Type', value: formData.finance_type === 'PCP' ? 'PCP' : 'HP' });
      financeRows.push({ label: 'Term', value: `${formData.term || 60} months` });
      financeRows.push({ label: 'Application', value: formData.application_type === 'Business' ? 'Business' : 'Private' });
      if (formData.finance_type === 'PCP') {
        financeRows.push({ label: 'Est. Annual Mileage', value: `${(formData.annual_mileage || 10000).toLocaleString('en-GB')} miles` });
      }

      // ----- Bank -----
      const bankRows = [
        { label: 'Bank Name', value: formData.bank_name },
        { label: 'Sort Code', value: formData.sort_code },
        { label: 'Account No.', value: formData.account_number || '-' },
        { label: 'Time with Bank', value: `${formData.bank_years || 0} yrs, ${formData.bank_months || 0} mths` },
      ];

      // ----- Address cards (one per address entry — flows independently) -----
      const addressCards = (formData.addresses || []).map((addr, index) => ({
        title: index === 0 ? 'Current Address' : `Previous Address ${index}`,
        rows: [
          { label: 'Address', value: [addr.sub_building, addr.house_number, addr.house_name, addr.street, addr.town, addr.county, addr.postcode].filter(Boolean).join(', '), multi: true },
          { label: 'Status', value: addr.residential_status },
          { label: 'Time', value: `${addr.years || 0} yrs, ${addr.months || 0} mths` },
        ],
      }));

      // ----- Employment cards (one per entry — flows independently) -----
      const empHistory = (formData.employment_history && formData.employment_history.length > 0)
        ? formData.employment_history
        : [{ employer: formData.employer, job_title: formData.job_title, employment_status: formData.employment_status, years: formData.employment_years, months: formData.employment_months, employer_phone: formData.employer_phone, employer_address_same_as_home: formData.employer_address_same_as_home, employer_address: formData.employer_address }];
      const employmentCards = empHistory.map((emp, index) => {
        const empStatus = index === 0 ? formData.employment_status : emp.employment_status;
        const sameAsHome = emp.employer_address_same_as_home || (index === 0 && formData.employer_address_same_as_home);
        const empAddr = emp.employer_address || (index === 0 ? formData.employer_address : null);
        const empAddrStr = sameAsHome
          ? 'Same as home'
          : (empAddr && (empAddr.postcode || empAddr.street || empAddr.town))
              ? [empAddr.sub_building, empAddr.building_number, empAddr.house_name || empAddr.building, empAddr.street, empAddr.town, empAddr.county, empAddr.postcode].filter(Boolean).join(', ')
              : '';
        const phone = emp.employer_phone || (index === 0 ? formData.employer_phone : '');
        const rows = [
          { label: 'Status', value: empStatus },
          { label: 'Employer', value: emp.employer || (index === 0 ? formData.employer : '') },
          { label: 'Job Title', value: emp.job_title || (index === 0 ? formData.job_title : '') },
          { label: 'Duration', value: `${emp.years || 0} yrs, ${emp.months || 0} mths` },
        ];
        if (empAddrStr) rows.push({ label: 'Emp. Address', value: empAddrStr, multi: true });
        if (phone) rows.push({ label: 'Emp. Phone', value: phone });
        return {
          title: index === 0 ? 'Current Employment' : `Previous Employment ${index}`,
          rows,
        };
      });

      // ----- Income & Affordability -----
      const incomeRows = [
        formData.gross_annual_income && { label: 'Gross Annual', value: money(formData.gross_annual_income), emphasise: true },
        formData.gross_monthly_income && { label: 'Gross Monthly', value: money(formData.gross_monthly_income) },
        formData.monthly_income && { label: 'Net Monthly', value: money(formData.monthly_income) },
        ...additionalIncomeLines(formData).map((line, i) => ({ label: i === 0 ? 'Other Income' : '', value: line, multi: line.length > 26 })),
        additionalIncomeLines(formData).length > 1 && { label: 'Other Inc. Total', value: additionalIncomeTotal(formData), emphasise: true },
        formData.monthly_mortgage_rent && { label: 'Rent/Mortgage', value: money(formData.monthly_mortgage_rent) },
      ].filter(Boolean);

      // ============================================================
      // RENDER
      // ============================================================
      // Design intent: the PDF should read like a re-fill guide — someone
      // re-entering the application into another broker system would want
      // to see ALL addresses grouped together, then ALL employments,
      // rather than "current address on page 1, previous address on page 2".
      //
      // Layout:
      //   Row 1: [Customer]  |  [Vehicle]
      //   Row 2: [Finance Summary] | [Income & Affordability]
      //   Row 3: DUAL-COLUMN STACK — left column = ALL addresses stacked,
      //          right column = ALL employments stacked. Same start-Y.
      //   Full:  Business Details, Additional Notes
      //   Last:  Bank Details (full-width, at end)

      // Detect application type once — layout differs for business apps:
      // the Business acts as the "Customer" (Row 1 top-left) and the applicant
      // becomes the "Director" (secondary card). PDF is also filename'd by
      // business name below.
      const isBusiness = formData.application_type === 'Business' && formData.business_details;
      const bd = isBusiness ? formData.business_details : null;
      const ba = isBusiness ? (bd.business_address || {}) : null;

      // ----- Rows 1 & 2 (money-critical + core details on page 1 top) -----
      // Business layout : Row 1 = [Business (Customer)  |  Director]
      // Personal layout : Row 1 = [Customer            |  Vehicle]
      if (isBusiness) {
        const bizRows = [
          { label: 'Business Name', value: bd.business_name, emphasise: true },
          { label: 'Business Type', value: bd.business_type },
          { label: 'Industry', value: bd.industry },
          bd.registration_number && { label: 'Reg. Number', value: bd.registration_number },
          bd.vat_number && { label: 'VAT Number', value: bd.vat_number },
          bd.main_telephone && { label: 'Telephone', value: bd.main_telephone },
          bd.date_of_incorporation && { label: 'Incorporated', value: bd.date_of_incorporation },
          (ba.postcode || ba.street) && { label: 'Business Addr.', value: [ba.sub_building, ba.building_number, ba.building, ba.street, ba.town, ba.county, ba.postcode].filter(Boolean).join(', '), multi: true },
          (ba.postcode || ba.street) && { label: 'Time at Addr.', value: `${ba.years || 0} yrs, ${ba.months || 0} mths` },
          ba.premises_type && { label: 'Premises', value: ba.premises_type },
        ].filter(Boolean);
        drawTwoCards(
          { title: 'Customer Details', rows: bizRows },
          { title: 'Director Details', rows: customerRows },
        );
        drawTwoCards(
          { title: 'Vehicle Details', rows: vehicleRows },
          { title: 'Finance Summary', rows: financeRows },
        );
      } else {
        drawTwoCards(
          { title: 'Customer Details', rows: customerRows },
          { title: 'Vehicle Details', rows: vehicleRows },
        );
        drawTwoCards(
          { title: 'Finance Summary', rows: financeRows },
          incomeRows.length > 0 ? { title: 'Income & Affordability', rows: incomeRows } : null,
        );
      }

      // Row 3: dual-column stack — all addresses left, all employments right.
      // Each column grows independently; if either column would overflow the
      // page, both jump to a new page together to preserve the pairing.
      const drawDualColumnStack = (leftCards, rightCards) => {
        if (leftCards.length === 0 && rightCards.length === 0) return;
        const cardW = halfWidth;
        const gap = 4;
        const leftX = margin;
        const rightX = margin + halfWidth + 6;
        let leftY = yPos;
        let rightY = yPos;
        let li = 0;
        let ri = 0;
        // Draw in interleaved order but track independent column Ys — the
        // shorter column always renders next so the columns stay balanced
        // and page breaks stay coordinated.
        while (li < leftCards.length || ri < rightCards.length) {
          const nextLeft = leftCards[li];
          const nextRight = rightCards[ri];
          const leftH = nextLeft ? measureCard(cardW, nextLeft.title, nextLeft.rows) : Infinity;
          const rightH = nextRight ? measureCard(cardW, nextRight.title, nextRight.rows) : Infinity;
          // Which column has room + a pending card?
          const drawLeftNext = nextLeft && (!nextRight || leftY <= rightY);
          if (drawLeftNext) {
            if (leftY + leftH > pageBottom) {
              // Overflow: coordinate both columns to a new page
              doc.addPage();
              leftY = 20;
              rightY = 20;
              continue;
            }
            drawCardAt(leftX, leftY, cardW, nextLeft.title, nextLeft.rows);
            leftY += leftH + gap;
            li++;
          } else if (nextRight) {
            if (rightY + rightH > pageBottom) {
              doc.addPage();
              leftY = 20;
              rightY = 20;
              continue;
            }
            drawCardAt(rightX, rightY, cardW, nextRight.title, nextRight.rows);
            rightY += rightH + gap;
            ri++;
          }
        }
        yPos = Math.max(leftY, rightY) + 1;
      };
      drawDualColumnStack(addressCards, employmentCards);

      // ----- Income & Affordability card (Business only) — Personal already
      // renders it in Row 2; Business renders it below the director stack
      // since Row 2 was consumed by Vehicle+Finance.
      if (isBusiness && incomeRows.length > 0) {
        drawFullCard({ title: 'Income & Affordability', rows: incomeRows });
      }

      // ----- Business Details already rendered at top of page 1 as
      // "Customer Details" for business apps — no duplicate needed here.

      // ----- Additional Notes (full-width, only if present) -----
      if (formData.additional_notes && formData.additional_notes.trim()) {
        drawFullCard({
          title: 'Additional Notes',
          rows: [{ label: '', value: formData.additional_notes.trim(), multi: true }],
        });
      }

      // ----- Bank Details LAST (full-width) — user feedback: bank info
      // isn't always provided and doesn't need to sit on page 1.
      drawFullCard({ title: 'Bank Details', rows: bankRows });



      // ---- Footer on all pages ----
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFillColor(brandColor.r, brandColor.g, brandColor.b);
        doc.rect(0, 294, pageWidth, 3, 'F');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(160, 160, 160);
        doc.text(`${sourceName} | Powered by ApplyDrive.uk`, margin, 290);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, 290, { align: 'right' });
      }

      return doc;
  };

  const handleExportPDF = () => {
    try {
      const doc = generatePDFDoc();
      const sourceSlug = source?.slug || 'Application';
      // Filename: business name for Business apps, applicant name for Personal.
      // Sanitise for filesystem-safe output (letters/numbers/dash/underscore only).
      const sanitise = (s) => (s || '').trim().replace(/[^\w\-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
      const isBusinessApp = formData.application_type === 'Business' && formData.business_details?.business_name;
      const nameForFile = isBusinessApp
        ? sanitise(formData.business_details.business_name) || 'Business'
        : `${sanitise(formData.first_name) || 'App'}_${sanitise(formData.surname) || ''}`.replace(/_+$/, '');
      const dateStr = new Date().toISOString().split('T')[0];
      doc.save(`${sourceSlug}_${nameForFile}_${dateStr}.pdf`);
      toast.success('PDF exported successfully!');
      axios.post(`${API_URL}/track/pdf-download`).catch(() => {});
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF: ' + error.message);
    }
  };

  if (isSubmitted) {
    if (submissionViaFallback) {
      return (
        <div className="text-center py-12" data-testid="success-message-fallback">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-amber-600" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Application Captured
          </h2>
          <p className="text-slate-600 mb-6 max-w-md mx-auto leading-relaxed">
            We couldn&rsquo;t reach the broker right now, but your details have been captured
            and sent to your dealer who will follow up with you shortly to confirm your application.
          </p>
          <Button
            onClick={resetForm}
            className="brand-bg  text-white"
            data-testid="start-new-application-btn"
          >
            Start New Application
          </Button>
        </div>
      );
    }
    return (
      <div className="text-center py-12" data-testid="success-message">
        <div className="w-20 h-20 brand-bg-medium rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-10 h-10 brand-text" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Application Submitted!
        </h2>
        {submissionResult?.enquiry_reference && (
          <p className="text-lg brand-text font-mono mb-2">
            Reference: {submissionResult.enquiry_reference}
          </p>
        )}
        <p className="text-slate-600 mb-6">
          Your application has been sent to {source?.name || source?.company_name || 'the dealer'}. They will be in touch soon.
        </p>
        <Button
          onClick={resetForm}
          className="brand-bg  text-white"
          data-testid="start-new-application-btn"
        >
          Start New Application
        </Button>
      </div>
    );
  }

  const Section = ({ icon: Icon, title, children, stepIndex, hasMissing, missingFields, headerBadge }) => (
    <div className={`border rounded-lg overflow-hidden ${hasMissing ? 'border-red-300 bg-red-50/30' : 'border-slate-200'}`}>
      <div className={`flex items-center justify-between p-4 border-b ${hasMissing ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
        <div className="flex items-center gap-3 flex-wrap">
          <Icon className={`w-5 h-5 ${hasMissing ? 'text-red-500' : 'brand-text'}`} />
          <h3 className="font-semibold text-slate-900">{title}</h3>
          {headerBadge}
          {hasMissing && (
            <span className="text-xs text-red-600 font-medium">
              (Missing: {missingFields || 'required fields'})
            </span>
          )}
        </div>
        {stepIndex != null && <Button
          variant="ghost"
          size="sm"
          onClick={() => handleEditSection(stepIndex)}
          className={`gap-1 ${hasMissing ? 'text-red-600 hover:text-red-700' : 'text-slate-600 hover:brand-text'}`}
          data-testid={`edit-${title.toLowerCase().replace(/\s/g, '-')}`}
        >
          <Edit2 className="w-4 h-4" />
          Edit
        </Button>}
      </div>
      <div className="p-4 space-y-2">{children}</div>
    </div>
  );

  // Field component with optional clickable behavior for missing required fields
  // and an optional `warning` chip rendered below the value (e.g. for fields
  // that are PRESENT but FORMATTED INCORRECTLY — like an OCR-extracted reg
  // plate that doesn't match a UK pattern). Using a chip on Review keeps the
  // problem visible even when the user Skip-to-Reviewed and never saw the
  // per-step amber hint on the wizard form itself.
  const Field = ({ label, value, required, jumpToStep, warning }) => {
    const isMissing = required && !value;
    // Empty optional fields with a jumpTo target get a soft amber "Click to add"
    // affordance — non-blocking, just an easy way to fill in stuff like rent
    // that often isn't on the original proposal but a dealer wants to add later.
    const isOptionalMissing = !required && !value && jumpToStep !== undefined;
    const isClickable = (isMissing || isOptionalMissing || warning) && jumpToStep !== undefined;

    const handleClick = () => {
      if (isClickable) {
        handleEditSection(jumpToStep);
      }
    };

    let valueColorClass;
    let displayValue;
    if (isMissing) {
      valueColorClass = 'text-red-500 underline';
      displayValue = 'Click to fix →';
    } else if (isOptionalMissing) {
      valueColorClass = 'text-amber-600 underline';
      displayValue = 'Click to add →';
    } else if (warning) {
      // Field has a value but the value is invalid — show it in amber so the
      // user's eye is drawn to it (e.g. malformed reg "9B3 XDC").
      valueColorClass = 'text-amber-700 font-semibold underline decoration-amber-400';
      displayValue = value || '-';
    } else {
      valueColorClass = 'text-slate-900';
      displayValue = value || '-';
    }

    let hoverClass = '';
    if (isMissing) hoverClass = 'cursor-pointer hover:bg-red-100 p-1 -m-1 rounded transition-colors';
    else if (isOptionalMissing || warning) hoverClass = 'cursor-pointer hover:bg-amber-50 p-1 -m-1 rounded transition-colors';

    return (
      <div className={hoverClass} onClick={handleClick}>
        <div className="flex justify-between text-sm">
          <span className={isMissing ? 'text-red-500 font-medium' : 'text-slate-500'}>
            {label}{required && ' *'}
          </span>
          <span className={`font-medium ${valueColorClass}`}>
            {displayValue}
          </span>
        </div>
        {warning && (
          <div
            className="mt-1 text-xs text-amber-700 italic flex items-center gap-1.5"
            data-testid={`review-field-warning-${label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
            <span>{warning} <span className="opacity-70 not-italic">— Click to fix →</span></span>
          </div>
        )}
      </div>
    );
  };

  // Step indices for each field type (updated for new Application Type step at index 3)
  const FIELD_STEPS = {
    dealer: 0,
    vehicle: 1,
    financeType: 2,
    applicationType: 3,
    drivingLicence: 4,
    maritalStatus: 5,
    passport: 6,
    nationality: 7,
    dob: 8,
    personalContact: 9,
    address: 10,
    employmentStatus: 11,
    employmentDetails: 12,
    income: 13,
    bank: 14
  };

  // Check for missing mandatory fields in each section
  const personalMissingList = [];
  if (!formData.title) personalMissingList.push('Title');
  if (!formData.first_name?.trim()) personalMissingList.push('First Name');
  if (!formData.surname?.trim()) personalMissingList.push('Surname');
  if (!formData.date_of_birth || formData.date_of_birth.includes('//')) personalMissingList.push('Date of Birth');
  if (!formData.marital_status) personalMissingList.push('Marital Status');
  if (!formData.driving_licence_type) personalMissingList.push('Driving Licence');
  const personalMissing = personalMissingList.length > 0;
  
  const contactMissingList = [];
  if (!formData.mobile_number?.trim()) contactMissingList.push('Mobile');
  if (!formData.email_address?.trim()) contactMissingList.push('Email');
  if (formData.email_address && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address)) contactMissingList.push('Valid Email');
  const contactMissing = contactMissingList.length > 0;
  
  const addressMissingList = [];
  if (!formData.addresses?.[0]?.postcode?.trim()) addressMissingList.push('Postcode');
  if (!formData.addresses?.[0]?.house_number?.trim() && !formData.addresses?.[0]?.house_name?.trim()) addressMissingList.push('House Number/Name');
  if (!formData.addresses?.[0]?.street?.trim()) addressMissingList.push('Street');
  if (!formData.addresses?.[0]?.town?.trim()) addressMissingList.push('Town');
  if (!formData.addresses?.[0]?.residential_status) addressMissingList.push('Residential Status');
  // Check all addresses for house number/name
  if (formData.addresses?.length > 1) {
    formData.addresses.forEach((addr, i) => {
      if (i === 0) return; // Already checked above
      if (!addr?.house_number?.trim() && !addr?.house_name?.trim()) {
        addressMissingList.push(`Previous ${i} House Number/Name`);
      }
    });
  }
  // Address history duration check — most lenders require 3 years minimum. Catches
  // the case where a user paste-extracted a single address with 0/0 years and
  // skipped straight to Review via the dealer-step shortcut, bypassing the
  // per-step address validation.
  const totalAddressYears = (formData.addresses || []).reduce(
    (sum, addr) => sum + (parseFloat(addr?.years) || 0) + ((parseFloat(addr?.months) || 0) / 12),
    0
  );
  if (totalAddressYears < 3) {
    const shortBy = (3 - totalAddressYears).toFixed(1);
    addressMissingList.push(`Address History (need ${shortBy} more year${shortBy === '1.0' ? '' : 's'})`);
  }
  const addressMissing = addressMissingList.length > 0;
  
  const employmentMissingList = [];
  if (!formData.employment_status) employmentMissingList.push('Status');
  if (['Full-Time Employment', 'Part-Time Employment', 'Self-Employed', 'Temporary/Contract', 'Armed Forces'].includes(formData.employment_status)) {
    if (!formData.employer?.trim()) employmentMissingList.push('Employer');
    if (!formData.job_title?.trim()) employmentMissingList.push('Job Title');
  }
  const employmentMissing = employmentMissingList.length > 0;
  
  const incomeMissingList = [];
  // Income is not required for Business applications (personal income may not be relevant)
  if (formData.application_type !== 'Business') {
    if (!formData.monthly_income?.trim() && !formData.gross_annual_income?.trim()) incomeMissingList.push('Income (Annual or Monthly)');
  }
  const incomeMissing = incomeMissingList.length > 0;

  // Bank details are OPTIONAL — soft-search/sub-prime lenders don't require them
  // upfront, and the customer may not have them to hand. Bank fields appear in
  // the completeness pill as amber "(if known)" chips, but submission is never
  // blocked on them.

  const hasMissingFields = personalMissing || contactMissing || addressMissing || employmentMissing || incomeMissing;

  // Soft applicant-email mismatch check — non-blocking. Surfaces an amber warning
  // inside the Personal Details section if the email's local-part looks like a
  // different person's name (e.g. johnsmith109@... for Annette Parker).
  const emailMismatch = detectEmailNameMismatch({
    email: formData.email_address,
    firstName: formData.first_name,
    surname: formData.surname,
  });
  const emailTypo = detectEmailTypo(formData.email_address);
  const completeness = computeFieldCompleteness(formData, { applicationMode, noReg });
  const isCreditLine = applicationMode === 'credit-line';

  return (
    <div className="space-y-6" data-testid="review-step">
      <div className="text-center">
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Review Your Application
        </h2>
        <p className="text-slate-600">Please check all details before submitting</p>
        {/* Field completeness badge — chips are clickable, each jumping straight to the
            wizard step where that field lives. Stays visible until submit. */}
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2" data-testid="field-completeness-badge">
          <span
            className={`inline-flex items-center px-3 py-1.5 rounded-full border bg-slate-50 border-slate-200 text-sm font-semibold ${completeness.got === completeness.total ? 'text-emerald-700' : 'text-slate-700'}`}
          >
            {completeness.got === completeness.total ? '\u2713 ' : ''}{completeness.got} of {completeness.total} fields populated
          </span>
          {completeness.missing.slice(0, 6).map((m) => {
            const cls = m.required
              ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100 hover:border-red-300'
              : 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:border-amber-300';
            return (
              <button
                type="button"
                key={`${m.label}-${m.step}`}
                onClick={() => handleEditSection(m.step)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors cursor-pointer ${cls}`}
                data-testid={`missing-chip-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                title={m.required ? `Required — go to step to fix: ${m.label}` : `Optional — add if known: ${m.label}`}
              >
                {m.label}{!m.required && ' (if known)'}
                <span aria-hidden="true">→</span>
              </button>
            );
          })}
          {completeness.missing.length > 6 && (
            <span className="text-xs text-slate-500" title={completeness.missing.map((m) => m.label).join(', ')}>
              +{completeness.missing.length - 6} more
            </span>
          )}
        </div>
        {hasMissingFields && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 font-medium">
              ⚠️ Some required fields are missing. Click on "Click to fix →" to jump directly to that field.
            </p>
          </div>
        )}
        {extractedData && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-300 rounded-lg" data-testid="extraction-disclaimer">
            <p className="text-sm text-amber-800 font-semibold mb-1">Important — Please Verify All Details</p>
            <p className="text-xs text-amber-700 leading-relaxed">
              Some of the information on this form was populated using AI-powered data extraction. 
              While we strive for accuracy, AI can make mistakes. You are responsible for reviewing 
              and confirming that all details are correct before submission. Click on any field to 
              make corrections.
            </p>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <Section icon={Building2} title="Dealer" stepIndex={source?.prefill_dealer_name ? null : 0}>
          <Field label="Dealer Name" value={formData.dealer_name} required jumpToStep={source?.prefill_dealer_name ? undefined : FIELD_STEPS.dealer} />
        </Section>

        {isCreditLine ? (
          <Section icon={Car} title="Vehicle Details" stepIndex={1}>
            <div className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 mb-2" data-testid="review-credit-line-notice">
              <p className="font-medium mb-1">Finance check only — no vehicle selected</p>
              <p className="text-xs">The applicant hasn&apos;t chosen a vehicle yet. Lender will assess credit eligibility based on personal and financial details only.</p>
            </div>
            <Field
              label="Amount to Borrow"
              value={formData.borrow_amount ? `£${parseFloat(formData.borrow_amount).toLocaleString('en-GB')}` : null}
              required
              jumpToStep={FIELD_STEPS.vehicle}
            />
          </Section>
        ) : (
        <Section
          icon={Car}
          title="Vehicle Details"
          stepIndex={1}
          headerBadge={
            formData.vat_qualifying ? (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300"
                data-testid="review-vat-qualifying-chip"
                title="VAT-qualifying — broker will receive a NET price and a +VAT note"
              >
                VAT Qualifying
              </span>
            ) : null
          }
        >
          <Field label="Vehicle Type" value={formData.vehicle_type} jumpToStep={FIELD_STEPS.vehicle} />
          {noReg && <Field label="Vehicle Description" value={formData.vehicle_description} jumpToStep={FIELD_STEPS.vehicle} />}
          {!noReg && <Field
            label="Registration"
            value={formData.vehicle_registration}
            jumpToStep={FIELD_STEPS.vehicle}
            warning={
              formData.vehicle_registration && !isValidUKVehicleReg(formData.vehicle_registration)
                ? `"${formData.vehicle_registration}" doesn't look like a UK plate (expected e.g. AB12 CDE)`
                : null
            }
          />}
          {formData.vehicle_make && <Field label="Make" value={formData.vehicle_make} jumpToStep={FIELD_STEPS.vehicle} />}
          {formData.vehicle_model && <Field label="Model" value={formData.vehicle_model} jumpToStep={FIELD_STEPS.vehicle} />}
          {formData.vehicle_derivative && <Field label="Derivative" value={formData.vehicle_derivative} />}
          {formData.vehicle_colour && <Field label="Colour" value={formData.vehicle_colour} />}
          {formData.vehicle_year && <Field label="Year" value={formData.vehicle_year} />}
          {formData.vehicle_fuel_type && <Field label="Fuel Type" value={formData.vehicle_fuel_type} />}
          {formData.vehicle_engine_capacity && <Field label="Engine" value={`${formData.vehicle_engine_capacity}cc`} />}
          {formData.vehicle_date_first_registered && <Field label="First Registered" value={formData.vehicle_date_first_registered} />}
          <Field label="Mileage" value={formData.vehicle_mileage} jumpToStep={FIELD_STEPS.vehicle} />
          {formData.mot_last_mileage && <Field label="Last MOT Mileage" value={`${Number(formData.mot_last_mileage).toLocaleString('en-GB')} mi (${formData.mot_last_date})`} />}
          {getMotMileageWarning(formData) && (
            <p className="col-span-full text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="review-mot-mileage-warning">{getMotMileageWarning(formData)}</p>
          )}
          <Field label="VAT Qualifying" value={formData.vat_qualifying ? 'Yes' : 'No'} jumpToStep={FIELD_STEPS.vehicle} />
          <Field 
            label="VAT Amount" 
            value={(() => {
              if (!formData.vat_qualifying) return '£0.00';
              const netPrice = parseFloat(formData.cash_price) || 0;
              const vatAmount = netPrice * 0.2;
              return `£${vatAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            })()} 
          />
          <Field
            label={formData.vat_qualifying ? 'Net Price (ex-VAT)' : 'Cash Price inc. VAT'}
            value={formData.cash_price ? `£${parseFloat(formData.cash_price).toLocaleString('en-GB')}${formData.vat_qualifying ? ' + VAT' : ''}` : null}
            jumpToStep={FIELD_STEPS.vehicle}
          />
          {formData.vat_qualifying && (
            <Field
              label="Gross Price (inc-VAT)"
              value={(() => {
                const netPrice = parseFloat(formData.cash_price) || 0;
                return `£${(netPrice * 1.2).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              })()}
            />
          )}
          <Field label="Deposit" value={`£${formData.deposit || 0}`} />
          <Field label="Part Exchange" value={`£${formData.part_exchange || 0}`} />
          <Field label="Settlement" value={`£${formData.settlement || 0}`} />
          <Field 
            label="Balance to Finance" 
            value={(() => {
              const rawPrice = parseFloat(formData.cash_price) || 0;
              const grossPrice = formData.vat_qualifying ? rawPrice * 1.2 : rawPrice;
              const deposit = parseFloat(formData.deposit) || 0;
              const partExchange = parseFloat(formData.part_exchange) || 0;
              const settlement = parseFloat(formData.settlement) || 0;
              const balance = grossPrice - deposit - partExchange + settlement;
              return `£${balance.toLocaleString('en-GB')}`;
            })()} 
          />
          <Field label="Finance Type" value={formData.finance_type === 'PCP' ? 'Personal Contract Purchase (PCP)' : 'Hire Purchase (HP)'} />
          <Field label="Term" value={`${formData.term || 60} months`} />
          {formData.finance_type === 'PCP' && (
            <Field label="Est. Annual Mileage" value={`${(formData.annual_mileage || 10000).toLocaleString()} miles`} jumpToStep={FIELD_STEPS.financeType} />
          )}
          <Field label="Application Type" value={formData.application_type === 'Business' ? 'Business' : 'Private Individual'} />
        </Section>
        )}

        {/* Business Details - Only shown for business applications */}
        {formData.application_type === 'Business' && formData.business_details && (
          <Section icon={Building2} title="Business Details" stepIndex={3}>
            <Field label="Business Name" value={formData.business_details.business_name} />
            <Field label="Business Type" value={formData.business_details.business_type} />
            <Field label="Industry" value={formData.business_details.industry} />
            <Field label="Registration No." value={formData.business_details.registration_number} />
            <Field label="VAT Number" value={formData.business_details.vat_number} />
            <Field label="Main Telephone" value={formData.business_details.main_telephone} />
            <Field label="Date of Incorporation" value={formData.business_details.date_of_incorporation} />
            {formData.business_details.business_address && (
              <>
                <Field 
                  label="Business Address" 
                  value={[
                    formData.business_details.business_address.building,
                    formData.business_details.business_address.street,
                    formData.business_details.business_address.town,
                    formData.business_details.business_address.county,
                    formData.business_details.business_address.postcode
                  ].filter(Boolean).join(', ')} 
                />
                <Field label="Premises Type" value={formData.business_details.business_address.premises_type} />
              </>
            )}
          </Section>
        )}

        <Section icon={User} title="Personal Details" stepIndex={9} hasMissing={personalMissing || contactMissing} missingFields={[...personalMissingList, ...contactMissingList].join(', ')}>
          <Field label="Name" value={`${formData.title} ${formData.first_name} ${formData.surname}`.trim()} required jumpToStep={FIELD_STEPS.personalContact} />
          <Field label="Gender" value={formData.gender} jumpToStep={FIELD_STEPS.personalContact} />
          <Field label="Date of Birth" value={formData.date_of_birth} required jumpToStep={FIELD_STEPS.dob} />
          {calculateAge(formData.date_of_birth) !== null && (
            <Field label="Age" value={`${calculateAge(formData.date_of_birth)} years old`} />
          )}
          <Field label="Nationality" value={formData.nationality} jumpToStep={FIELD_STEPS.nationality} />
          <Field label="Marital Status" value={formData.marital_status} required jumpToStep={FIELD_STEPS.maritalStatus} />
          <Field label="Driving Licence" value={formData.driving_licence_type} required jumpToStep={FIELD_STEPS.drivingLicence} />
          <Field label="UK Passport" value={formData.has_uk_passport === true ? 'Yes' : formData.has_uk_passport === false ? 'No' : '-'} jumpToStep={FIELD_STEPS.passport} />
          <Field label="Dependants" value={formData.dependants !== undefined && formData.dependants !== null ? formData.dependants.toString() : '0'} />
          <Field label="Mobile" value={formData.mobile_number} required jumpToStep={FIELD_STEPS.personalContact} />
          <Field label="Email" value={formData.email_address} required jumpToStep={FIELD_STEPS.personalContact} />
          {emailMismatch && (
            <div
              className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid="review-email-mismatch-warning"
              onClick={() => handleEditSection(FIELD_STEPS.personalContact)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') handleEditSection(FIELD_STEPS.personalContact); }}
              style={{ cursor: 'pointer' }}
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
              <span>
                This email contains a different name (<strong>{emailMismatch.detectedName}</strong>).
                Lenders may flag this — the email address must belong to the applicant.{' '}
                <span className="underline">Click to edit →</span>
              </span>
            </div>
          )}
          {emailTypo && (
            <div
              className="mt-2 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid="review-email-typo-warning"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
              <span className="flex-1">
                Possible email typo — did you mean <strong>{emailTypo.suggested}</strong>?
              </span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); updateFormData({ email_address: emailTypo.suggested }); }}
                className="text-amber-900 underline font-medium hover:text-amber-700"
                data-testid="review-email-typo-accept-btn"
              >
                Use this
              </button>
            </div>
          )}
        </Section>

        <Section icon={Home} title="Address" stepIndex={10} hasMissing={addressMissing} missingFields={addressMissingList.join(', ')}>
          {formData.addresses?.map((addr, index) => (
            <div key={index} className={index > 0 ? 'pt-2 border-t border-slate-100' : ''}>
              <Field 
                label={index === 0 ? 'Current Address' : `Previous ${index}`} 
                value={[addr.sub_building, addr.house_number, addr.house_name, addr.street, addr.town, addr.postcode].filter(Boolean).join(', ')} 
                required={index === 0}
                jumpToStep={FIELD_STEPS.address}
              />
              <Field label="Residential Status" value={addr.residential_status} required={index === 0} jumpToStep={FIELD_STEPS.address} />
              <Field label="Duration" value={`${addr.years || 0} years, ${addr.months || 0} months`} />
              {index === 0 && (
                <Field
                  label="Monthly Rent / Mortgage"
                  value={formData.monthly_mortgage_rent ? `£${formData.monthly_mortgage_rent}` : null}
                  jumpToStep={FIELD_STEPS.income}
                />
              )}
            </div>
          ))}
        </Section>

        <Section icon={Briefcase} title="Employment" stepIndex={11} hasMissing={employmentMissing || incomeMissing} missingFields={[...employmentMissingList, ...incomeMissingList].join(', ')}>
          {formData.employment_history && formData.employment_history.length > 0 ? (
            formData.employment_history.map((emp, index) => {
              // Use employment_history values, with fallback to main fields for first entry
              const empStatus = index === 0 ? formData.employment_status : emp.employment_status;
              const empEmployer = emp.employer || (index === 0 ? formData.employer : '');
              const empJobTitle = emp.job_title || (index === 0 ? formData.job_title : '');
              const empYears = emp.years !== undefined && emp.years !== null ? emp.years : (index === 0 ? formData.employment_years : 0);
              const empMonths = emp.months !== undefined && emp.months !== null ? emp.months : (index === 0 ? formData.employment_months : 0);
              const empPhone = emp.employer_phone || (index === 0 ? formData.employer_phone : '');
              const empSameAsHome = emp.employer_address_same_as_home !== undefined ? emp.employer_address_same_as_home : (index === 0 ? formData.employer_address_same_as_home : false);
              
              // Check if emp.employer_address has actual values
              const hasEmpAddress = emp.employer_address && (emp.employer_address.postcode || emp.employer_address.building_number || emp.employer_address.street || emp.employer_address.town);
              const empAddress = hasEmpAddress ? emp.employer_address : (index === 0 ? formData.employer_address : null);
              
              return (
                <div key={index} className={index > 0 ? 'pt-2 border-t border-slate-100' : ''}>
                  <Field 
                    label={index === 0 ? 'Current Employment' : `Previous ${index}`} 
                    value={empStatus} 
                  />
                  <Field label="Employer" value={empEmployer} />
                  <Field label="Job Title" value={empJobTitle} />
                  <Field label="Duration" value={`${empYears || 0} years, ${empMonths || 0} months`} />
                  {index === 0 && <Field label="Annual Income (before tax)" value={formData.gross_annual_income ? `£${formData.gross_annual_income}` : null} />}
                  {index === 0 && <Field label="Gross Monthly Income" value={formData.gross_monthly_income ? `£${formData.gross_monthly_income}` : null} />}
                  {index === 0 && <Field label="Net Monthly Income" value={formData.monthly_income ? `£${formData.monthly_income}` : null} />}
                  {index === 0 && additionalIncomeLines(formData).length > 0 && <Field label="Other Income" value={<AdditionalIncomeList formData={formData} />} />}
                  <Field label="Employer Phone" value={empPhone} />
                  {empSameAsHome ? (
                    <Field 
                      label="Employer Address" 
                      value={formData.addresses?.[0] ? [
                        formData.addresses[0].house_number,
                        formData.addresses[0].street,
                        formData.addresses[0].town,
                        formData.addresses[0].postcode
                      ].filter(Boolean).join(', ') + ' (Same as home)' : 'Same as home address'} 
                    />
                  ) : (
                    <Field 
                      label="Employer Address" 
                      value={empAddress ? [
                        empAddress.building_number,
                        empAddress.street,
                        empAddress.town,
                        empAddress.postcode
                      ].filter(Boolean).join(', ') : null} 
                    />
                  )}
                </div>
              );
            })
          ) : (
            <>
              <Field label="Status" value={formData.employment_status} />
              <Field label="Employer" value={formData.employer} />
              <Field label="Job Title" value={formData.job_title} jumpToStep={FIELD_STEPS.employmentDetails} />
              <Field label="Duration" value={`${formData.employment_years || 0} years, ${formData.employment_months || 0} months`} />
              <Field label="Annual Income (before tax)" value={formData.gross_annual_income ? `£${formData.gross_annual_income}` : null} jumpToStep={FIELD_STEPS.income} />
              <Field label="Gross Monthly Income" value={formData.gross_monthly_income ? `£${formData.gross_monthly_income}` : null} jumpToStep={FIELD_STEPS.income} />
              <Field label="Net Monthly Income" value={formData.monthly_income ? `£${formData.monthly_income}` : null} jumpToStep={FIELD_STEPS.income} />
              {additionalIncomeLines(formData).length > 0 && <Field label="Other Income" value={<AdditionalIncomeList formData={formData} />} jumpToStep={FIELD_STEPS.income} />}
              <Field label="Employer Phone" value={formData.employer_phone} />
              {formData.employer_address_same_as_home ? (
                <Field 
                  label="Employer Address" 
                  value={formData.addresses?.[0] ? [
                    formData.addresses[0].house_number,
                    formData.addresses[0].street,
                    formData.addresses[0].town,
                    formData.addresses[0].postcode
                  ].filter(Boolean).join(', ') + ' (Same as home)' : 'Same as home address'} 
                />
              ) : (
                <Field 
                  label="Employer Address" 
                  value={formData.employer_address ? [
                    formData.employer_address.building_number,
                    formData.employer_address.street,
                    formData.employer_address.town,
                    formData.employer_address.postcode
                  ].filter(Boolean).join(', ') : null} 
                />
              )}
            </>
          )}
        </Section>

        <Section icon={CreditCard} title="Bank Details" stepIndex={14}>
          <Field label="Bank Name" value={formData.bank_name || 'Not Provided'} />
          <Field label="Sort Code" value={formData.sort_code || 'Not Provided'} />
          <Field label="Account Number" value={formData.account_number || 'Not Provided'} />
          <Field
            label="Time with Bank"
            value={(formData.bank_years || formData.bank_months) ? `${formData.bank_years || 0} years, ${formData.bank_months || 0} months` : 'Not Provided'}
          />
        </Section>

        {formData.additional_notes && (
          <Section icon={FileDown} title="Additional Notes" stepIndex={15}>
            <div className="text-sm text-slate-700 whitespace-pre-wrap">
              {formData.additional_notes}
            </div>
          </Section>
        )}
      </div>

      {/* Pre-submit validation errors (parity with per-step Next button gating) */}
      {validationErrors.length > 0 && (
        <div
          className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg"
          data-testid="pre-submit-validation-errors"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-red-800 mb-2">
                Please fix {validationErrors.length === 1 ? 'this' : `these ${validationErrors.length}`} before submitting:
              </h4>
              <ul className="space-y-1.5">
                {validationErrors.map((v, index) => (
                  <li key={`${v.stepId}-${index}`} className="text-sm text-red-700">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingFromReview(true);
                        goToStep(v.stepIndex);
                      }}
                      className="text-left underline decoration-red-300 hover:decoration-red-600 hover:text-red-900"
                      data-testid={`pre-submit-fix-${v.stepId}`}
                    >
                      <strong>{v.label}:</strong> {v.error} <span className="opacity-70">— Click to fix →</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Submission Errors */}
      {submissionErrors && submissionErrors.length > 0 && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="submission-errors">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-800 mb-2">Please fix the following issues:</h4>
              <ul className="space-y-1">
                {submissionErrors.map((error, index) => (
                  <li key={index} className="text-sm text-red-700">
                    <strong>{error.field}:</strong> {error.messages.join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Consent Notice */}
      <div className="pt-4 border-t border-slate-200 space-y-4">
        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
          <p className="text-sm text-slate-600 leading-relaxed">
            By submitting your application, you agree to our{' '}
            {source?.terms_url ? (
              <a href={source.terms_url} target="_blank" rel="noopener noreferrer" className="brand-text hover:underline font-medium">terms and conditions</a>
            ) : (
              <span className="font-medium">terms and conditions</span>
            )}
            {' '}and{' '}
            {source?.privacy_policy_url ? (
              <a href={source.privacy_policy_url} target="_blank" rel="noopener noreferrer" className="brand-text hover:underline font-medium">privacy policy</a>
            ) : (
              <a href={`/s/${source?.slug}/privacy`} target="_blank" rel="noopener noreferrer" className="brand-text hover:underline font-medium">privacy policy</a>
            )}.
          </p>
        </div>
      </div>

      <div className="pt-4 space-y-3">
        {/* Operator Mode — consent confirmation block */}
        {isOperator && (
          <div className="border-2 border-purple-300 bg-purple-50 rounded-lg p-4 space-y-3" data-testid="operator-consent-block">
            <div className="flex items-start gap-2">
              <ShieldAlert className="w-5 h-5 text-purple-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-purple-900">Operator submission — consent required</p>
                <p className="text-xs text-purple-700 mt-0.5">You are submitting this application on behalf of <strong>{source?.name}</strong>. Please confirm consent below.</p>
              </div>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={consentConfirmed}
                onChange={(e) => setConsentConfirmed(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-purple-400 text-purple-700 focus:ring-purple-500"
                data-testid="operator-consent-checkbox"
              />
              <span className="text-sm text-purple-900">
                I confirm that <strong>{source?.name}</strong> has obtained the customer's consent to submit this application.
              </span>
            </label>
            <div>
              <label className="text-xs text-purple-700 font-medium block mb-1">Consent reference (optional)</label>
              <input
                type="text"
                value={consentRef}
                onChange={(e) => setConsentRef(e.target.value)}
                placeholder="e.g. WhatsApp 14:32 from John Smith"
                maxLength={200}
                className="w-full bg-white border border-purple-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="operator-consent-ref"
              />
            </div>
          </div>
        )}

        {/* Application Health Check pill — proactive go/no-go indicator just
            above the Submit button. Red = blockers exist (also surfaces in
            the click-driven [pre-submit-validation-errors] panel); Amber =
            optional fields missing or soft heuristics like email typo / name
            mismatch are flagged; Green = clean submission. The same
            validateAllSteps() function powers the per-step Next button so
            the three views (Next button, pill, click-driven panel) never
            disagree. */}
        {(() => {
          const liveErrors = validateAllSteps(formData, applicationMode, { allowNoReg: source?.submission_mode === 'pdf_email' });
          const softWarnings =
            (emailTypo ? 1 : 0) +
            (emailMismatch ? 1 : 0) +
            (completeness?.missing?.filter((m) => !m.required).length || 0);
          if (liveErrors.length > 0) {
            return (
              <div
                data-testid="application-health-red"
                className="flex items-center gap-3 px-4 py-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-800"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium">
                  {liveErrors.length} issue{liveErrors.length === 1 ? '' : 's'} to fix before submitting — click <strong>Submit</strong> below for the full list.
                </span>
              </div>
            );
          }
          if (softWarnings > 0) {
            return (
              <div
                data-testid="application-health-amber"
                className="flex items-center gap-3 px-4 py-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800"
              >
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium">
                  {softWarnings} soft warning{softWarnings === 1 ? '' : 's'} — fine to submit but worth a glance.
                </span>
              </div>
            );
          }
          return (
            <div
              data-testid="application-health-green"
              className="flex items-center gap-3 px-4 py-3 mb-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800"
            >
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-medium">All sections look good — ready to submit ✓</span>
            </div>
          );
        })()}

        {/* Direct submission - Primary action */}
        <Button
          onClick={handleSubmitToAutoConvert}
          disabled={isSubmittingToAutoConvert || (isOperator && !consentConfirmed)}
          className="w-full brand-bg  text-white font-semibold py-4 text-lg disabled:opacity-50"
          data-testid="submit-to-autoconvert-btn"
        >
          {isSubmittingToAutoConvert ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              {source?.submission_mode === 'pdf_email' ? 'Sending Application...' : 'Submitting...'}
            </>
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              {source?.submission_mode === 'pdf_email' ? 'Submit Application' : `Submit to ${source?.name || 'Broker'}`}
            </>
          )}
        </Button>

        {/* Export PDF Button */}
        <Button
          onClick={handleExportPDF}
          variant="outline"
          className="w-full brand-border brand-text hover:brand-bg-light font-semibold py-4 text-lg"
          data-testid="export-pdf-btn"
        >
          <FileDown className="w-5 h-5 mr-2" />
          Export as PDF
        </Button>

        {/* Advanced options — collapsed by default. Currently only exposes the
            "hold from lender automation" toggle for Artis submissions. Kept low-key
            (grey text button) so it doesn't distract the standard flow. */}
        {source?.broker_system === 'artis' && source?.show_hold_automation !== false && (
          <div className="mt-2" data-testid="prevent-automation-section">
            <button
              type="button"
              onClick={() => setShowMoreOptions(!showMoreOptions)}
              className="flex items-center gap-1 mx-auto text-xs text-slate-500 hover:text-slate-700 py-2 transition-colors"
              data-testid="more-options-toggle"
            >
              More options
              <ChevronDown className={`w-3 h-3 transition-transform ${showMoreOptions ? 'rotate-180' : ''}`} />
            </button>
            {showMoreOptions && (
              <div className="mt-1 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formData.prevent_automation}
                    onChange={(e) => updateFormData({ prevent_automation: e.target.checked })}
                    className="mt-0.5 w-3.5 h-3.5 text-slate-600 border-slate-300 rounded focus:ring-slate-400"
                    data-testid="prevent-automation-checkbox"
                  />
                  <div className="flex-1">
                    <div className="text-xs font-medium text-slate-700">Hold from lender automation</div>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                      Proposal lands in Artis but does not auto-submit to lenders — manual review only.
                    </p>
                  </div>
                </label>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewStep;
